/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { ComboBox, IComboBox, IComboBoxOption, IComboBoxProps, IComboBoxStyles } from '@fluentui/react/lib/ComboBox';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { partial } from 'src/core/utils/function';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { HierarchicalEntity, HierarchicalEntityChild } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { LuisId } from 'src/luis/types/Resource';

const scopedTheme = getScopedTheme('AdvancedOptionsPane');

const messages = defineMessages({
    emptyChildEntity: {
        id: 'luis.intentLabelingSidePane.advancedOptions.compositeEntity.emptyChildEntity',
        defaultMessage: 'Select a child entity'
    },
    childEntity: {
        id: 'luis.intentLabelingSidePane.advancedOptions.compositeEntity.childEntity',
        defaultMessage: 'child entity'
    },
    clearButton: {
        id: 'luis.intentLabelingSidePane.advancedOptions.compositeEntity.clearButton',
        defaultMessage: 'Clear child entity'
    }
});

const comboboxStyles = classNamesFunction<IComboBoxProps, IComboBoxStyles>()({
    label: { textTransform: 'uppercase', fontSize: '10px', color: scopedTheme.titleTextColor },
    input: { paddingRight: 24 }
});

const clearIconStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { position: 'absolute', top: 25, width: 30, height: 30, right: 32 },
    icon: { fontSize: 14 }
});

export type HierarchicalEntityAdvancedOptionsProps = {
    entity: HierarchicalEntity;
    selectedEntityId: LuisId;
    onSelectChildEntity: (entity: Entity, childEntityId: LuisId) => void;
};

/**
 * Renders advanced options for a role-based entity.
 * Regex and prebuilt entities for now.
 */
export const HierarchicalEntityAdvancedOptions = React.memo((props: HierarchicalEntityAdvancedOptionsProps) => {
    const { entity, onSelectChildEntity, selectedEntityId } = props;
    const clearButtonRef = React.useRef(getId('HierarchicalEntityAdvancedOptions'));
    const clearButtonTooltipRef = React.useRef(getId('HierarchicalEntityAdvancedOptions'));

    const { children } = entity;

    const { localize } = useLocalization();

    const options = children.map(({ id, name }: HierarchicalEntityChild) => ({
        key: id,
        text: name,
        ariaLabel: name
    }));

    const handleChange = (_event: React.FormEvent<IComboBox>, option: IComboBoxOption) => {
        if (option) {
            onSelectChildEntity(entity, option.key);
        }
    };

    return (
        <Stack styles={{ root: { position: 'relative' } }}>
            <ComboBox
                allowFreeform
                options={options}
                openOnKeyboardFocus
                styles={comboboxStyles}
                useComboBoxAsMenuWidth
                onChange={handleChange}
                selectedKey={selectedEntityId}
                label={localize(messages.childEntity)}
                calloutProps={{ calloutMaxHeight: 300 }}
                placeholder={localize(messages.emptyChildEntity)}
            />
            <LuisTooltip
                content={localize(messages.clearButton)}
                calloutProps={{ target: `#${clearButtonRef.current}` }}
                id={clearButtonTooltipRef.current}>
                <IconButton
                    id={clearButtonRef.current}
                    onClick={partial(onSelectChildEntity, entity, null)}
                    iconProps={{ iconName: 'Clear' }}
                    styles={clearIconStyles}
                    aria-describedby={clearButtonTooltipRef.current}
                />
            </LuisTooltip>
        </Stack>
    );
});
